import {
    Avatar,
    ButtonGradientPrimary,
    ButtonSecondary,
    Checkbox,
    Colors,
    Icon,
    SelectFilled,
} from '@amelio/ui-library';
import { useAtomValue } from 'jotai';
import { useMemo, useState } from 'react';
import { Trans } from 'react-i18next';

import type { RosterEmployee } from '@/api/competency-services/profile-assignment/profileAssignmentService';
import type { CompetencyProfile } from '@/types/competency/CompetencyProfile';

import { useAssignProfile } from '@/api/competency-services/profile-assignment';
import { companyAtom } from '@/atoms/company';
import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { useError } from '@/hooks/utils/useError';
import { jobAndDepartmentLabel } from '@/pages/Competencies/components/employees/personDisplay';
import { fullName, initials, MUTED, NAVY } from '@/pages/CompetencyProfilesAdmin/helpers';
import { showSuccessToast } from '@/plugins/Toast';
import { getCompanyAttributeList } from '@/utils/company';
import { getTextByLanguage } from '@/utils/localization';
import { getProfilePictureUrl } from '@/utils/user';

type SelectionMode = 'all' | 'department' | 'role';
type Step = 'employees' | 'profile' | 'review';

type BatchAssignmentStepProps = {
    // Employees WITHOUT a profile (the only valid batch targets).
    unassignedEmployees: RosterEmployee[];
    // Employees who already have the chosen profile, for the review summary.
    assignedEmployees: RosterEmployee[];
    profiles: CompetencyProfile[];
    onClose: () => void;
    onAssigned: () => void;
};

// Three-step batch assignment: select employees (all / by department / by job title,
// with per-employee checkboxes) → choose a profile → review + progress. Uses
// assignProfile once per employee, with a progress bar. Rendered as a full-page step
// (not a modal); the top Back button leaves the batch via onClose, the inner Back
// button steps between sub-steps.
export const BatchAssignmentStep = ({
    unassignedEmployees,
    assignedEmployees,
    profiles,
    onClose,
    onAssigned,
}: BatchAssignmentStepProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();
    const company = useAtomValue(companyAtom);
    const { getJobTitle, getDepartment } = useCompanyInformation();
    const { handleError } = useError();
    const { mutateAsync: assignProfile } = useAssignProfile();

    const companyAttributes = getCompanyAttributeList(company);
    const defaultDepartmentAttribute = t('common.department');
    const defaultJobTitleAttribute = t('common.jobTitle');
    const departmentLabel = companyAttributes.departments?.name || defaultDepartmentAttribute;
    const jobTitleLabel = companyAttributes.jobTitles?.name || defaultJobTitleAttribute;
    const departmentModeLabel = t('competencies.competencyProfilesAdmin.batch.modeByAttribute', {
        attribute: departmentLabel,
    });
    const roleModeLabel = t('competencies.competencyProfilesAdmin.batch.modeByAttribute', {
        attribute: jobTitleLabel,
    });

    const [step, setStep] = useState<Step>('employees');
    const [mode, setMode] = useState<SelectionMode>('all');
    const [selectedDepartments, setSelectedDepartments] = useState<Set<string>>(() => new Set());
    const [selectedRoles, setSelectedRoles] = useState<Set<string>>(() => new Set());
    const [excludedIds, setExcludedIds] = useState<Set<string>>(() => new Set());
    const [selectedProfileId, setSelectedProfileId] = useState('');
    const [isAssigning, setIsAssigning] = useState(false);
    const [progress, setProgress] = useState(0);

    const jobAndDepartment = (employee: RosterEmployee): string =>
        jobAndDepartmentLabel(employee, {
            getJobTitle,
            getDepartment,
            fallback: t('competencies.competencyProfilesAdmin.noJobDepartment'),
        });

    // Department/role options derived from the unassigned employees (label + id).
    const departmentOptions = useMemo(() => {
        const ids = [
            ...new Set(unassignedEmployees.map((e) => e.departmentId).filter(Boolean)),
        ] as string[];

        return ids.map((id) => ({ id, label: getDepartment(id) }));
    }, [unassignedEmployees, getDepartment]);

    const roleOptions = useMemo(() => {
        const ids = [
            ...new Set(unassignedEmployees.map((e) => e.jobTitleId).filter(Boolean)),
        ] as string[];

        return ids.map((id) => ({ id, label: getJobTitle(id) }));
    }, [unassignedEmployees, getJobTitle]);

    // Candidates after the mode filter (before per-employee exclusion).
    const candidates = useMemo(() => {
        if (mode === 'department') {
            return selectedDepartments.size === 0
                ? []
                : unassignedEmployees.filter(
                      (e) => e.departmentId && selectedDepartments.has(e.departmentId),
                  );
        }

        if (mode === 'role') {
            return selectedRoles.size === 0
                ? []
                : unassignedEmployees.filter(
                      (e) => e.jobTitleId && selectedRoles.has(e.jobTitleId),
                  );
        }

        return unassignedEmployees;
    }, [mode, unassignedEmployees, selectedDepartments, selectedRoles]);

    const targets = useMemo(
        () => candidates.filter((e) => !excludedIds.has(e.id)),
        [candidates, excludedIds],
    );

    const selectedProfile = profiles.find((profile) => profile.id === selectedProfileId);

    // Options for the lib SelectFilled profile picker: value = profile id, label = name
    // in the selected app language.
    const profileOptions = useMemo(
        () =>
            profiles.map((profile) => {
                const name = getTextByLanguage(profile.name, language);
                const skillCount = t(
                    'competencies.competencyProfilesAdmin.batch.profileSkillCount',
                    { count: profile.items.length },
                );

                return {
                    value: profile.id,
                    label: `${name} ${skillCount}`,
                };
            }),
        [profiles, language, t],
    );

    // Employees who already have the selected profile (for the review counts).
    const alreadyWithProfile = useMemo(
        () => assignedEmployees.filter((e) => e.assignedProfileId === selectedProfileId),
        [assignedEmployees, selectedProfileId],
    );

    const resetSelectionState = () => setExcludedIds(new Set());

    const toggleExclude = (id: string) => {
        setExcludedIds((current) => {
            const next = new Set(current);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }

            return next;
        });
    };

    const toggleDepartment = (id: string) => {
        setSelectedDepartments((current) => {
            const next = new Set(current);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }

            return next;
        });
        resetSelectionState();
    };

    const toggleRole = (id: string) => {
        setSelectedRoles((current) => {
            const next = new Set(current);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }

            return next;
        });
        resetSelectionState();
    };

    const handleAssign = async () => {
        if (!selectedProfileId || targets.length === 0) {
            return;
        }

        setIsAssigning(true);
        setProgress(0);

        try {
            // One assignment call per employee so the progress bar advances and a single
            // failure doesn't abort the whole batch silently. assignProfile replaces the
            // employee's profile (1 profile per employee).
            for (let index = 0; index < targets.length; index += 1) {
                await assignProfile({
                    profileId: selectedProfileId,
                    payload: { employeeIds: [targets[index].id] },
                });
                setProgress(Math.round(((index + 1) / targets.length) * 100));
            }

            showSuccessToast({
                message: t('competencies.competencyProfilesAdmin.toast.batchAssigned', {
                    count: targets.length,
                }),
            });
            onAssigned();
            onClose();
        } catch (error) {
            handleError({ error });
        } finally {
            setIsAssigning(false);
        }
    };

    const modes: Array<{ key: SelectionMode; label: string }> = [
        { key: 'all', label: t('competencies.competencyProfilesAdmin.batch.modeAll') },
        { key: 'department', label: departmentModeLabel },
        { key: 'role', label: roleModeLabel },
    ];

    const stepSubtitle =
        step === 'employees'
            ? t('competencies.competencyProfilesAdmin.batch.stepEmployees')
            : step === 'profile'
              ? t('competencies.competencyProfilesAdmin.batch.stepProfile')
              : t('competencies.competencyProfilesAdmin.batch.stepReview');

    return (
        <div
            className={
                'border-primary/20 ds-rounded-md border bg-white/80 !p-[24px] backdrop-blur-sm max-md:p-[16px]!'
            }
        >
            <div className={'ds-mb-2xl flex items-center justify-between gap-3'}>
                <div className={'ds-gap-md flex items-center'}>
                    <span
                        className={
                            'bg-primary/10 flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-full'
                        }
                    >
                        <Icon
                            name={'users-profile-2user'}
                            color={Colors.PRIMARY}
                            size={20}
                        />
                    </span>

                    <div>
                        <p
                            className={'text-subtitle font-semibold'}
                            style={{ color: NAVY }}
                        >
                            {t('competencies.competencyProfilesAdmin.batchAssignment')}
                        </p>

                        <p className={'text-navy/60 text-body-sm mt-[2px]'}>{stepSubtitle}</p>
                    </div>
                </div>

                <button
                    type={'button'}
                    onClick={onClose}
                    disabled={isAssigning}
                    className={
                        'text-navy/50 ds-rounded-sm ds-p-sm shrink-0 transition-colors hover:bg-[#f5f7f9] disabled:opacity-40'
                    }
                    aria-label={t('competencies.competencyProfilesAdmin.back')}
                    data-testid={'roster-batch-cancel'}
                >
                    <Icon
                        name={'essential-close'}
                        size={20}
                    />
                </button>
            </div>

            <div>
                {step === 'employees' && (
                    <div className={'ds-gap-lg flex flex-col'}>
                        <div className={'ds-gap-sm flex flex-wrap'}>
                            {modes.map((m) => (
                                <button
                                    key={m.key}
                                    type={'button'}
                                    onClick={() => {
                                        setMode(m.key);
                                        resetSelectionState();
                                    }}
                                    className={
                                        'ds-rounded-sm ds-px-lg ds-py-sm text-body-sm font-medium transition-colors max-md:whitespace-normal ' +
                                        (mode === m.key
                                            ? 'bg-primary text-white'
                                            : 'bg-[#f5f7f9] text-[#5E5E5E] hover:bg-[#eef1f4]')
                                    }
                                    data-testid={`roster-batch-mode-${m.key}`}
                                >
                                    {m.label}
                                </button>
                            ))}
                        </div>

                        {mode === 'department' && (
                            <div className={'ds-gap-sm flex flex-wrap'}>
                                {departmentOptions.map((option) => (
                                    <button
                                        key={option.id}
                                        type={'button'}
                                        onClick={() => toggleDepartment(option.id)}
                                        className={
                                            'ds-px-md text-caption rounded-full border py-[6px] font-medium transition-colors ' +
                                            (selectedDepartments.has(option.id)
                                                ? 'border-primary bg-primary text-white'
                                                : 'hover:border-primary/50 text-navy border-[#dbe0e6] bg-white')
                                        }
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        )}

                        {mode === 'role' && (
                            <div className={'ds-gap-sm flex flex-wrap'}>
                                {roleOptions.map((option) => (
                                    <button
                                        key={option.id}
                                        type={'button'}
                                        onClick={() => toggleRole(option.id)}
                                        className={
                                            'ds-px-md text-caption rounded-full border py-[6px] font-medium transition-colors ' +
                                            (selectedRoles.has(option.id)
                                                ? 'border-primary bg-primary text-white'
                                                : 'hover:border-primary/50 text-navy border-[#dbe0e6] bg-white')
                                        }
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        )}

                        <div
                            className={
                                'ds-rounded-md scrollbar-glass max-h-[60vh] divide-y divide-[#dbe0e6] overflow-y-auto border border-[#dbe0e6]'
                            }
                        >
                            {candidates.length === 0 ? (
                                <p
                                    className={'ds-p-2xl text-body-sm text-center'}
                                    style={{ color: MUTED }}
                                >
                                    {mode === 'department' && selectedDepartments.size === 0
                                        ? `${t('common.select')} ${departmentLabel.toLowerCase()}`
                                        : mode === 'role' && selectedRoles.size === 0
                                          ? `${t('common.select')} ${jobTitleLabel.toLowerCase()}`
                                          : t(
                                                'competencies.competencyProfilesAdmin.batch.noUnassigned',
                                            )}
                                </p>
                            ) : (
                                candidates.map((employee) => (
                                    <div
                                        key={employee.id}
                                        role={'button'}
                                        tabIndex={0}
                                        aria-label={`${fullName(employee)} - ${jobAndDepartment(employee)}`}
                                        className={
                                            'ds-gap-md ds-px-lg ds-py-md flex cursor-pointer items-center transition-colors hover:bg-[#f5f7f9]'
                                        }
                                        onClick={(e) => {
                                            const target = e.target as Element;
                                            const checkboxComponent = target.closest(
                                                '[data-testid^="roster-batch-employee-"]',
                                            );
                                            if (!checkboxComponent) {
                                                toggleExclude(employee.id);
                                            }
                                        }}
                                    >
                                        <Checkbox
                                            size={'sm'}
                                            className={
                                                'h-[18px]! w-[18px]! rounded-[6px]! border-[1.5px]!'
                                            }
                                            checked={!excludedIds.has(employee.id)}
                                            ariaLabel={fullName(employee)}
                                            onChange={() => toggleExclude(employee.id)}
                                            dataTestId={`roster-batch-employee-${employee.id}`}
                                        />

                                        <Avatar
                                            size={'3xs'}
                                            className={'h-[32px]! w-[32px]!'}
                                            bgColor={'default'}
                                            src={
                                                getProfilePictureUrl(employee.profilePictureUrl) ||
                                                undefined
                                            }
                                            initials={initials(employee)}
                                            alt={fullName(employee)}
                                        />

                                        <div className={'min-w-0 flex-1'}>
                                            <p
                                                className={'text-body-sm truncate font-medium'}
                                                style={{ color: NAVY }}
                                            >
                                                {fullName(employee)}
                                            </p>

                                            <p
                                                className={'text-caption truncate'}
                                                style={{ color: MUTED }}
                                            >
                                                {jobAndDepartment(employee)}
                                            </p>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        <p
                            className={'text-body-sm'}
                            style={{ color: MUTED }}
                            data-testid={'roster-batch-selected-count'}
                        >
                            <Trans
                                i18nKey={'competencies.competencyProfilesAdmin.batch.selectedCount'}
                                count={targets.length}
                                components={{
                                    bold: (
                                        <span
                                            className={'font-semibold'}
                                            style={{ color: NAVY }}
                                        />
                                    ),
                                }}
                            />
                        </p>
                    </div>
                )}

                {step === 'profile' && (
                    <div className={'ds-gap-lg flex flex-col'}>
                        <p
                            className={'text-body-sm font-medium'}
                            style={{ color: NAVY }}
                        >
                            {t('competencies.competencyProfilesAdmin.batch.profileLabel')}
                        </p>

                        <div className={'w-full max-w-md'}>
                            <SelectFilled
                                value={selectedProfileId}
                                options={profileOptions}
                                onChange={(value: string) => setSelectedProfileId(value)}
                                placeholder={t(
                                    'competencies.competencyProfilesAdmin.batch.profilePlaceholder',
                                )}
                                ariaLabel={t(
                                    'competencies.competencyProfilesAdmin.batch.profileLabel',
                                )}
                                searchable
                                searchPlaceholder={t(
                                    'competencies.competencyProfilesAdmin.selector.searchPlaceholder',
                                )}
                                emptyText={t('common.noResults')}
                                dataTestId={'roster-batch-profile-select'}
                                className={
                                    'ds-py-md ds-px-lg ds-rounded-md h-auto! border-[#e2e8f0]! bg-white'
                                }
                            />
                        </div>

                        {selectedProfileId && (
                            <div className={'ds-gap-md flex flex-wrap'}>
                                <span
                                    className={
                                        'ds-rounded-sm ds-px-lg ds-py-sm ds-gap-sm text-body-sm inline-flex items-center border border-[#23a47b33] bg-[#23a47b1a] font-medium text-[#23a47b]'
                                    }
                                >
                                    <Icon
                                        name={'essential-checkmark'}
                                        size={16}
                                        color={'inherit'}
                                    />

                                    {t('competencies.competencyProfilesAdmin.batch.alreadyHave', {
                                        count: alreadyWithProfile.length,
                                    })}
                                </span>

                                <span
                                    className={
                                        'ds-rounded-sm ds-px-lg ds-py-sm ds-gap-sm text-body-sm text-step-rail-turquoise inline-flex items-center border border-[#0dd3bc33] bg-[#0dd3bc1a] font-medium'
                                    }
                                >
                                    <Icon
                                        name={'users-profile-2user'}
                                        size={16}
                                        color={'inherit'}
                                    />

                                    {t('competencies.competencyProfilesAdmin.batch.willReceive', {
                                        count: targets.length,
                                    })}
                                </span>
                            </div>
                        )}
                    </div>
                )}

                {step === 'review' && (
                    <div className={'ds-gap-lg flex flex-col'}>
                        <div className={'responsive-grid-1-3-sm ds-gap-lg'}>
                            <div className={'ds-rounded-sm ds-p-lg bg-[#f5f7f9]'}>
                                <p
                                    className={'ds-mb-xs text-caption'}
                                    style={{ color: MUTED }}
                                >
                                    {t('competencies.competencyProfilesAdmin.batch.summaryProfile')}
                                </p>

                                <p
                                    className={'text-body-sm font-semibold'}
                                    style={{ color: NAVY }}
                                >
                                    {selectedProfile
                                        ? getTextByLanguage(selectedProfile.name, language)
                                        : ''}
                                </p>
                            </div>

                            <div className={'ds-rounded-sm ds-p-lg bg-[#e9f9f6]'}>
                                <p className={'ds-mb-xs text-caption text-[#0a8c80]'}>
                                    {t('competencies.competencyProfilesAdmin.batch.summaryAlready')}
                                </p>

                                <p className={'text-body-sm font-semibold text-[#0a8c80]'}>
                                    {alreadyWithProfile.length}
                                </p>
                            </div>

                            <div className={'bg-primary/10 ds-rounded-sm ds-p-lg'}>
                                <p className={'text-primary ds-mb-xs text-caption'}>
                                    {t('competencies.competencyProfilesAdmin.batch.summaryReceive')}
                                </p>

                                <p className={'text-primary text-body-sm font-semibold'}>
                                    {targets.length}
                                </p>
                            </div>
                        </div>

                        <div
                            className={
                                'ds-gap-md ds-rounded-sm ds-p-lg flex border border-[#f0c14b]/40 bg-[#f7863b1a] max-md:flex-wrap'
                            }
                        >
                            <Icon
                                name={'essential-warning-2'}
                                color={Colors.WARNING}
                                size={20}
                            />

                            <p
                                className={'text-body-sm'}
                                style={{ color: NAVY }}
                            >
                                {t('competencies.competencyProfilesAdmin.batch.reviewSentence', {
                                    count: alreadyWithProfile.length + targets.length,
                                    profile: selectedProfile
                                        ? getTextByLanguage(selectedProfile.name, language)
                                        : '',
                                })}
                            </p>
                        </div>

                        <div
                            className={
                                'ds-rounded-md scrollbar-glass max-h-[55vh] divide-y divide-[#dbe0e6] overflow-y-auto border border-[#dbe0e6]'
                            }
                        >
                            {targets.map((employee) => (
                                <div
                                    key={employee.id}
                                    className={'ds-gap-md ds-px-lg ds-py-md flex items-center'}
                                >
                                    <Avatar
                                        size={'3xs'}
                                        className={'h-[32px]! w-[32px]!'}
                                        bgColor={'default'}
                                        src={
                                            getProfilePictureUrl(employee.profilePictureUrl) ||
                                            undefined
                                        }
                                        initials={initials(employee)}
                                        alt={fullName(employee)}
                                    />

                                    <div className={'min-w-0 flex-1'}>
                                        <p
                                            className={'text-body-sm truncate font-medium'}
                                            style={{ color: NAVY }}
                                        >
                                            {fullName(employee)}
                                        </p>

                                        <p
                                            className={'text-caption truncate'}
                                            style={{ color: MUTED }}
                                        >
                                            {jobAndDepartment(employee)}
                                        </p>
                                    </div>

                                    <span
                                        className={
                                            'bg-primary/10 text-primary ds-px-md ds-py-xs text-caption shrink-0 rounded-full font-medium'
                                        }
                                    >
                                        {t('competencies.competencyProfilesAdmin.batch.newBadge')}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {isAssigning && (
                            <div className={'ds-gap-sm flex flex-col'}>
                                <div
                                    className={'h-[8px] overflow-hidden rounded-full bg-[#eef1f4]'}
                                >
                                    <div
                                        className={'bg-primary h-full transition-all'}
                                        style={{ width: `${progress}%` }}
                                    />
                                </div>

                                <p
                                    className={'text-caption text-center'}
                                    style={{ color: MUTED }}
                                >
                                    {t('competencies.competencyProfilesAdmin.batch.assigning', {
                                        percent: progress,
                                    })}
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div
                className={
                    'ds-mt-xl ds-gap-md ds-pt-md flex items-center justify-between max-md:flex-wrap max-md:justify-start'
                }
            >
                {step !== 'employees' ? (
                    <ButtonSecondary
                        size={'md'}
                        className={'gap-2 border border-gray-300 text-sm'}
                        leftIcon={
                            <Icon
                                name={'arrow-arrow-left-1'}
                                size={16}
                            />
                        }
                        onClick={() => setStep(step === 'review' ? 'profile' : 'employees')}
                        disabled={isAssigning}
                        dataTestId={'roster-batch-back'}
                        fitContent={true}
                    >
                        {t('competencies.competencyProfilesAdmin.batch.back')}
                    </ButtonSecondary>
                ) : (
                    <span />
                )}

                {step === 'employees' && (
                    <ButtonGradientPrimary
                        size={'md'}
                        fitContent={true}
                        className={'gap-2 text-sm'}
                        disabled={targets.length === 0}
                        onClick={() => setStep('profile')}
                        dataTestId={'roster-batch-continue'}
                        rightIcon={
                            <Icon
                                name={'arrow-arrow-right-1'}
                                size={16}
                                color={Colors.WHITE}
                            />
                        }
                    >
                        {t('competencies.competencyProfilesAdmin.batch.continue')}
                    </ButtonGradientPrimary>
                )}

                {step === 'profile' && (
                    <ButtonGradientPrimary
                        size={'sm'}
                        className={
                            'ds-px-lg text-font-sm! h-auto! scale-100! gap-[8px]! py-[9px] max-md:whitespace-normal! md:min-w-auto!'
                        }
                        disabled={!selectedProfileId}
                        onClick={() => setStep('review')}
                        dataTestId={'roster-batch-review'}
                        rightIcon={
                            <Icon
                                name={'arrow-arrow-right-1'}
                                size={16}
                                color={Colors.WHITE}
                            />
                        }
                    >
                        {t('competencies.competencyProfilesAdmin.batch.reviewAssignment')}
                    </ButtonGradientPrimary>
                )}

                {step === 'review' && (
                    <ButtonGradientPrimary
                        size={'sm'}
                        className={
                            'ds-px-lg ds-py-sm text-font-sm! h-auto! scale-100! gap-[8px]! max-md:whitespace-normal!'
                        }
                        loading={isAssigning}
                        disabled={isAssigning || targets.length === 0}
                        onClick={() => void handleAssign()}
                        dataTestId={'roster-batch-confirm'}
                        leftIcon={
                            isAssigning ? undefined : (
                                <Icon
                                    name={'essential-checkmark'}
                                    size={16}
                                    color={Colors.WHITE}
                                />
                            )
                        }
                    >
                        {t('competencies.competencyProfilesAdmin.batch.confirm', {
                            count: targets.length,
                        })}
                    </ButtonGradientPrimary>
                )}
            </div>
        </div>
    );
};
